'use client';

import {
  amplitudeSendEvent,
  GTM_NOTSET_STRING,
  getScreenName,
  pushDataLayer,
} from '@musinsa/frontend-utils';
import { Divider, Typography, type TypographyProps } from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { track } from '@musinsa/packages-event-snowplow';
import Link from 'next/link';
import { type FC, forwardRef } from 'react';
import { AgreementModal } from '../../agreements';
import type { AgreementType } from '../../agreements/utils/agreementVersions';
import { SNOWPLOW_SCHEME } from '../../snowplow';
import { useNavigate } from '../hooks';
import { pathsMusinsa } from '../paths';

interface GuideLink {
  id: string;
  label: string;
  url: string;
}

const GUIDE_LINKS_29CM: GuideLink[] = [
  {
    id: 'service_guide',
    label: '서비스 안내',
    url: 'https://kindly-success-bae.notion.site/29cm-268f9524906080bda962cb391299c861',
  },
  {
    id: 'how_to_use',
    label: '활동 방법',
    url: 'https://kindly-success-bae.notion.site/268f95249060814db5a3f068ee1cefd7',
  },
  {
    id: 'faq',
    label: 'FAQ 자주 묻는 질문',
    url: 'https://kindly-success-bae.notion.site/FAQ-268f952490608170b5d2e09183765476',
  },
];

const GUIDE_LINKS_MUSINSA: GuideLink[] = [
  {
    id: 'service_guide',
    label: '서비스 안내',
    url: 'https://kindly-success-bae.notion.site/beta-479fe7f0a12b4ed5af5989f6029973c7',
  },
  {
    id: 'how_to_use',
    label: '활동 방법',
    url: 'https://docs.google.com/presentation/d/1K1cNSbtAXp853mljXE2XX4hzE07E5b5i/edit?usp=sharing&ouid=117446585115386785322&rtpof=true&sd=true',
  },
  {
    id: 'faq',
    label: 'FAQ 자주 묻는 질문',
    url: 'https://kindly-success-bae.notion.site/FAQ-241f95249060803d8e52cfa5651e9b65',
  },
  {
    id: 'coupon_guide',
    label: '쿠폰 사용 및 활용 가이드',
    url: pathsMusinsa.campaignCouponGuide(),
  },
];

const FooterLink = forwardRef<HTMLDivElement, TypographyProps>(({ children, ...props }, ref) => {
  return (
    <Typography
      ref={ref}
      variant="body13pxReg"
      textDecoration="underline"
      colorScheme="gray-600"
      className="cursor-pointer"
      {...props}
    >
      {children}
    </Typography>
  );
});
FooterLink.displayName = 'FooterLink';

interface FooterProps {
  description?: string;
  onAgreementClick?: (type: AgreementType) => void;
}

export const Footer: FC<FooterProps> = ({ description, onAgreementClick }) => {
  const { isMobile, service } = useDeviceInfo();
  const { navigate } = useNavigate();
  const isMusinsaWeb = service === SERVICE.MUSINSA && !isMobile;
  const guideLinks = service === SERVICE.MUSINSA ? GUIDE_LINKS_MUSINSA : GUIDE_LINKS_29CM;

  const handleAgreementClick = (type: AgreementType) => () => {
    onAgreementClick?.(type);
  };

  const handleClickGuideLink = (data: GuideLink) => () => {
    if (data.url) {
      if (data.url.startsWith('http')) {
        window.open(data.url, '_blank');
      } else {
        navigate(data.url);
      }
    }

    if (service === SERVICE['29CM']) {
      track({
        schema: SNOWPLOW_SCHEME.CLICK_BUTTON,
        data: {
          page_name: 'curator_mypage',
          section_name: 'GuideLinkGroup',
          section_title: 'GuideLinkGroup',
          position: 3,
          button_id: data.id,
          button_name: data.label,
        },
      });
    }

    if (service === SERVICE.MUSINSA) {
      amplitudeSendEvent({
        action_name: 'click',
        button_name: 'guide_button',
        component_name: 'button',
        event_name: 'click_button',
        from: getScreenName(),
        name: data.label,
        section_name: 'mypage_dashboard',
      });
      pushDataLayer('click_button', {
        brand_id: GTM_NOTSET_STRING,
        button_id: 'guide_button',
        button_name: data.label,
        index: GTM_NOTSET_STRING,
        section_index: '6',
        section_name: 'mypage_dashboard',
      });
    }
  };

  const guideRows: GuideLink[][] = [];
  for (let i = 0; i < guideLinks.length; i += 2) {
    guideRows.push(guideLinks.slice(i, i + 2));
  }

  return (
    <div className="flex flex-col w-full bg-gray-100">
      {description && (
        <div className="px-[16px]! pt-[24px]!">
          <Typography variant="body13pxReg" colorScheme="gray-600">
            {description}
          </Typography>
        </div>
      )}

      <div className="px-[16px]! pt-[24px]! pb-[6px]!">
        <Typography variant="body14pxMed">큐레이터 지원</Typography>
      </div>
      {guideRows.map((row) => (
        <div key={row[0].id} className="flex w-full">
          {row.map((link) => (
            <button
              key={link.id}
              type="button"
              // SID-4418: 큐레이터 지원 메뉴 버튼 높이 30px 고정(패딩 의존 X)
              className="flex-1 flex items-center text-left px-[16px]! h-[30px]! cursor-pointer"
              onClick={handleClickGuideLink(link)}
            >
              <Typography variant="body13pxReg">{link.label}</Typography>
            </button>
          ))}
        </div>
      ))}

      <div className="px-[16px]! py-[12px]!">
        <Divider variant="line" />
      </div>

      <div className="flex items-center gap-[8px]! px-[16px]! pt-[4px]! pb-[72px]!">
        {isMusinsaWeb ? (
          <FooterLink asChild onClick={handleAgreementClick('terms')}>
            <Link href={pathsMusinsa.terms()} scroll>
              이용약관
            </Link>
          </FooterLink>
        ) : (
          <AgreementModal type="terms">
            <FooterLink onClick={handleAgreementClick('terms')}>이용약관</FooterLink>
          </AgreementModal>
        )}

        <Typography variant="body13pxReg" colorScheme="gray-600">
          ·
        </Typography>

        {isMusinsaWeb ? (
          <FooterLink asChild onClick={handleAgreementClick('policy')}>
            <Link href={pathsMusinsa.policy()}>운영 정책</Link>
          </FooterLink>
        ) : (
          <AgreementModal type="policy">
            <FooterLink onClick={handleAgreementClick('policy')}>운영 정책</FooterLink>
          </AgreementModal>
        )}
      </div>
    </div>
  );
};
